// ─────────────────────────────────────────────────────────────────────────────
// Sentry/GlitchTip SDK init for the 2hives.ai SPA — card #3095.
//
// We point @sentry/react at the self-hosted GlitchTip backend at
// https://errors.2hives.ai (Sentry-protocol-compatible). The DSN is fetched
// from the runtime-config envelope (window.__2HIVES_CONFIG__.sentry?.dsn).
// If the envelope has no DSN, init is a NO-OP — the SDK is not loaded into
// the global error pipeline at all, so a misconfigured CDN-edge cannot send
// errors to the wrong place.
//
// Design rules:
//   - tracesSampleRate = 0  → no performance traces. Beta = errors only.
//   - No session replay (PII risk + bundle cost).
//   - beforeSend scrubs PII fields and drops well-known noise.
//   - release pinned to import.meta.env.VITE_GIT_SHA (set by Deploy Frontend
//     workflow); falls back to "dev" so local dev does not poison release tags.
//   - environment = runtimeConfig.sentry?.environment ?? import.meta.env.MODE.
//   - Tag user.id once AuthContext rehydrates with cognito sub
//     (see setSentryUser / clearSentryUser).
// ─────────────────────────────────────────────────────────────────────────────
import * as Sentry from "@sentry/react";

import { getRuntimeConfig } from "../types/RuntimeConfig";

/** Errors that originate outside our code and are not actionable. */
const NOISE_PATTERNS: ReadonlyArray<RegExp> = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /Non-Error promise rejection captured/i,
  /Loading chunk \d+ failed/i,
  /Importing a module script failed/i,
];

/**
 * Recursively walk an event and drop keys that may carry PII.
 *
 * The Sentry SDK already strips most of `request.headers`, but our error
 * envelopes can include user objects (we set them via `setUser` once the
 * Cognito profile loads). We keep only the opaque `id` (cognito sub) and
 * strip `email`, `name`, `username`, and `ip_address`.
 */
function scrubPii<T extends Sentry.ErrorEvent>(event: T): T {
  if (event.user) {
    const { id } = event.user;
    event.user = id ? { id } : undefined;
  }
  // Some browser extensions inject extra context with the user's display
  // name — defensive double-pass.
  if (event.contexts && typeof event.contexts === "object") {
    for (const key of Object.keys(event.contexts)) {
      const ctx = event.contexts[key];
      if (ctx && typeof ctx === "object") {
        delete (ctx as Record<string, unknown>).email;
        delete (ctx as Record<string, unknown>).name;
        delete (ctx as Record<string, unknown>).username;
      }
    }
  }
  return event;
}

/**
 * beforeSend hook — exported so tests can call it directly without booting
 * the whole SDK.
 */
export function sentryBeforeSend(
  event: Sentry.ErrorEvent,
  hint?: Sentry.EventHint,
): Sentry.ErrorEvent | null {
  const original = (hint?.originalException ?? null) as unknown;
  const message =
    typeof original === "string"
      ? original
      : original && typeof original === "object" && "message" in original
        ? String((original as { message?: unknown }).message ?? "")
        : event.message ?? "";

  if (message && NOISE_PATTERNS.some((re) => re.test(message))) {
    return null;
  }

  return scrubPii(event);
}

let _initialized = false;

/**
 * Initialize Sentry from the runtime-config envelope.
 *
 * Returns true on init, false on no-op. Idempotent — calling twice is safe.
 * Exported so callers in tests / story books can detect the no-op path.
 */
export function initSentryFromRuntimeConfig(): boolean {
  if (_initialized) return true;

  const cfg = getRuntimeConfig();
  const dsn = cfg?.sentry?.dsn?.trim();
  if (!dsn) {
    // No DSN → no SDK. This is the expected path during local dev and any
    // environment where /v1/runtime-config does not surface a DSN.
    return false;
  }

  const env =
    (cfg?.sentry as { environment?: string } | undefined)?.environment
    ?? import.meta.env.MODE
    ?? "prod";

  const release =
    (import.meta.env.VITE_GIT_SHA as string | undefined)?.trim() || "dev";

  Sentry.init({
    dsn,
    environment: env,
    release,
    tracesSampleRate: 0,           // errors only during beta
    // Defence: belt-and-braces against accidental session-replay enablement
    // — we intentionally do NOT add `Sentry.replayIntegration()`.
    integrations: [],
    sendDefaultPii: false,
    beforeSend: sentryBeforeSend,
  });

  _initialized = true;
  return true;
}

/** Tag the current Sentry scope with the logged-in user's cognito sub. */
export function setSentryUser(sub: string | null | undefined): void {
  if (!_initialized) return;
  if (sub) {
    Sentry.setUser({ id: sub });
  } else {
    Sentry.setUser(null);
  }
}

/** Test-only: reset internal init flag. Not exported through the barrel. */
export function __resetSentryForTests(): void {
  _initialized = false;
}
